import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/localization/localization';
import { useLanguageAwareRender } from '../hooks/useLanguageAwareRender';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import AnalyzerPage from '../components/AnalyzerPage';
import { tableShellSx, responsiveTableContainerSx } from '../components/common';
import { standardizeItems } from '../utils/versionManager';
import { logger } from '../utils/logger';
import { usePerformanceMonitor } from '../utils/performanceMonitor';
import CVAnalyzerService from '../services/cvAnalyzerService';

// Removed manual persistence import; persistence handled by shared AnalyzerPage and VersionContext

// Steps will be defined directly in the component to ensure they update when language changes

/**
 * CV Analyzer Page - Refactored to use shared AnalyzerPage component
 */
function CVAnalyzerPage({ userId, viewOnly = false, onComplete, onStatusChange }) {
  // Get language context
  const { currentLanguage } = useLanguage();

  // Use standardized language-aware render hook
  const renderKey = useLanguageAwareRender('CVAnalyzerPage');

  // Add performance monitoring
  const performanceMetrics = usePerformanceMonitor(
    'CVAnalyzerPage',
    {
      userId,
      viewOnly,
      currentLanguage
    },
    {
      renderKey
    }
  );

  // Log performance warnings
  useEffect(() => {
    if (performanceMetrics.isExcessiveRenders) {
      logger.warn(
        'PERFORMANCE',
        'CVAnalyzerPage experiencing excessive re-renders',
        {
          renderCount: performanceMetrics.renderCount,
          windowCount: performanceMetrics.renderWindowCount,
          userId,
          viewOnly
        }
      );
    }
  }, [performanceMetrics, userId, viewOnly]);

  // Get initial state for the CV analyzer
  const getInitialState = () => ({
    editingCompetency: null,
    isNewCompetency: false,
    selectedCompetencyIndex: null,
    // Filter states
    categoryFilter: '',
    proficiencyFilter: '',
    categoryFilterAnchorEl: null,
    proficiencyFilterAnchorEl: null
  });

  // Process uploaded files (extract text if needed)
  const processFiles = (acceptedFiles, setText) => {
    // For now, just handle TXT files directly
    // In a real implementation, would extract text from various file formats
    const textFile = acceptedFiles.find(file => file.type === 'text/plain');
    if (textFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setText(e.target.result);
      };
      reader.readAsText(textFile);
    }
  };

  // Handle editing a competency
  const handleEditCompetency = (competency, index, updateCustomState) => {
    updateCustomState({
      editingCompetency: { ...competency },
      isNewCompetency: false,
      selectedCompetencyIndex: index
    });
  };

  // Handle adding a new competency
  const _handleAddCompetency = (updateCustomState) => {
    updateCustomState({
      editingCompetency: {
        competency: '',
        category: 'Competences techniques',
        proficiency: 'Intermediaire',
        evidence: ''
      },
      isNewCompetency: true,
      selectedCompetencyIndex: null
    });
  };

  // Handle removing a competency
  const handleRemoveCompetency = (index, analysisResult, setAnalysisResult) => {
    // Check if analysisResult and competencies exist
    if (!analysisResult || !analysisResult.competencies) {
      console.error('Cannot remove competency: analysisResult or competencies is null/undefined');
      return;
    }

    // Create a new array without the removed competency
    const updatedCompetencies = [...analysisResult.competencies];
    updatedCompetencies.splice(index, 1);

    // Update the analysis result with the new competencies
    setAnalysisResult({
      ...analysisResult,
      competencies: updatedCompetencies
    });
  };

  // Handle saving the edited competency (new signature for AnalyzerPage)
  const handleSaveEdit = useCallback((editingIndex, updatedItem, analysisResult, setAnalysisResult) => {
    if (!analysisResult) {
      throw new Error('Cannot save edit: analysisResult is null/undefined');
    }

    const competencies = [...(analysisResult.competencies || [])];

    if (editingIndex === -1) {
      // Add new competency
      competencies.push(updatedItem);
    } else {
      // Update existing competency
      competencies[editingIndex] = updatedItem;
    }

    // Standardize the data to ensure all required fields
    const standardizedData = standardizeItems({ ...analysisResult, competencies }, 'cv');

    // Update the analysis result
    setAnalysisResult(standardizedData);
    return standardizedData;
  }, []);

  // Helper function to get unique values for filters
  const getUniqueValues = (data, key) => {
    if (!data || !Array.isArray(data)) return [];
    return [...new Set(data.map(item => item[key]).filter(Boolean))].sort();
  };

  // Render the competencies data table
  const renderDataDisplay = (
    analysisResult,
    customState,
    updateCustomState,
    setOpenEditDialog,
    setAnalysisResult,
    _handleDeleteSkill,
    _setActiveStep,
    startEditing,
    _stopEditing
  ) => {
    if (!analysisResult) return null;

    const allCompetencies = analysisResult.competencies || [];

    // Get unique categories and proficiency levels for filters
    const categories = getUniqueValues(allCompetencies, 'category');
    const proficiencyLevels = getUniqueValues(allCompetencies, 'proficiency');

    // Apply filters
    const competencies = allCompetencies.filter(comp => {
      const categoryMatch = !customState.categoryFilter || comp.category === customState.categoryFilter;
      const proficiencyMatch = !customState.proficiencyFilter || comp.proficiency === customState.proficiencyFilter;
      return categoryMatch && proficiencyMatch;
    });

    logger.info('[CV ANALYZER] Filtered competencies:', competencies.length,
      'Category filter:', customState.categoryFilter,
      'Proficiency filter:', customState.proficiencyFilter);

    // Handle filter menu close for each column
    const handleCategoryFilterClose = () => {
      updateCustomState(prevState => ({ ...prevState, categoryFilterAnchorEl: null }));
    };

    const handleProficiencyFilterClose = () => {
      updateCustomState(prevState => ({ ...prevState, proficiencyFilterAnchorEl: null }));
    };

    // Handle filter selection for each column
    const handleCategoryFilterSelect = (value) => {
      updateCustomState(prevState => ({
        ...prevState,
        categoryFilter: value,
        categoryFilterAnchorEl: null
      }));
    };

    const handleProficiencyFilterSelect = (value) => {
      updateCustomState(prevState => ({
        ...prevState,
        proficiencyFilter: value,
        proficiencyFilterAnchorEl: null
      }));
    };

    const cvTableColumnSx = {
      competency: { width: { xs: '34%', md: '22%' }, minWidth: { xs: 180, md: 220 } },
      category: { width: { xs: '20%', md: '16%' }, minWidth: { xs: 140, md: 170 } },
      proficiency: { width: { xs: '18%', md: '13%' }, minWidth: { xs: 110, md: 130 }, whiteSpace: 'nowrap' },
      evidence: { width: { xs: '28%', md: '41%' }, minWidth: { xs: 220, md: 340 } },
      actions: { width: { xs: '1%', md: '8%' }, minWidth: 96, whiteSpace: 'nowrap' }
    };

    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 2 }}>
            {t('cvAnalyzer.competenciesAnalysisResults')}
          </Typography>

          {/* Filter status indicator - only show if filters are active */}
          {(customState.categoryFilter || customState.proficiencyFilter) && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Chip
                icon={<FilterAltIcon />}
                label={`${t('common.filtered')}: ${customState.categoryFilter ? `${t('cvAnalyzer.category')}=${customState.categoryFilter}` : ''} ${customState.proficiencyFilter ? `${t('cvAnalyzer.proficiency')}=${customState.proficiencyFilter}` : ''}`}
                onDelete={() => updateCustomState(prevState => ({ ...prevState, categoryFilter: '', proficiencyFilter: '' }))}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          )}

          {!viewOnly && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  // We'll use a default category that will be updated when the dialog loads
                  const newCompetency = {
                    competency: '',
                    category: 'Competences techniques', // This will be updated when categories load
                    proficiency: 'Intermediaire',
                    evidence: ''
                  };
                  _handleAddCompetency(updateCustomState);
                  startEditing(newCompetency, -1); // -1 indicates new item
                }}
              >
                {t('cvAnalyzer.addCompetency')}
              </Button>
            </Box>
          )}
        </Box>

        {/* Competencies table */}
        <Box sx={tableShellSx}>
          <TableContainer component={Paper} sx={responsiveTableContainerSx}>
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                minWidth: { xs: 760, md: 900 }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={cvTableColumnSx.competency}>{t('cvAnalyzer.competency')}</TableCell>
                  <TableCell sx={cvTableColumnSx.category}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={(e) => {
                      // Handle category filter menu click
                        const anchorEl = e.currentTarget;
                        // Set anchor for filter menu
                        updateCustomState(prevState => ({
                          ...prevState,
                          categoryFilterAnchorEl: anchorEl
                        }));
                      }}>
                      {t('cvAnalyzer.category')}
                      <FilterListIcon fontSize="small" sx={{ ml: 0.5, color: customState.categoryFilter ? 'primary.main' : 'action.disabled' }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={cvTableColumnSx.proficiency}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={(e) => {
                      // Handle proficiency filter menu click
                        const anchorEl = e.currentTarget;
                        // Set anchor for filter menu
                        updateCustomState(prevState => ({
                          ...prevState,
                          proficiencyFilterAnchorEl: anchorEl
                        }));
                      }}>
                      {t('cvAnalyzer.proficiency')}
                      <FilterListIcon fontSize="small" sx={{ ml: 0.5, color: customState.proficiencyFilter ? 'primary.main' : 'action.disabled' }} />
                    </Box>
                  </TableCell>
                  <TableCell sx={cvTableColumnSx.evidence}>{t('cvAnalyzer.evidence')}</TableCell>
                  {!viewOnly && <TableCell className="table-actions-cell" sx={cvTableColumnSx.actions}>{t('common.actions')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {competencies.map((competency, index) => {
                // Get the status from the comparison data if available
                  const status = competency.status || 'unchanged';
                  const isAdded = status === 'added';
                  const isModified = status === 'modified';

                  // Custom styling based on status
                  const rowStyle = {
                    backgroundColor:
                    isAdded ? 'rgba(76, 175, 80, 0.1)' : // Green for added
                      isModified ? 'rgba(33, 150, 243, 0.1)' : // Blue for modified
                        'inherit'
                  };

                  return (
                    <TableRow key={index} sx={rowStyle}>
                      <TableCell sx={cvTableColumnSx.competency}>
                        {competency.competency}
                        {isAdded && (
                          <Chip
                            label={t('cvAnalyzer.newStatus')}
                            size="small"
                            color="success"
                            sx={{ ml: 1 }}
                          />
                        )}
                        {isModified && (
                          <Chip
                            label={t('cvAnalyzer.modifiedStatus')}
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={cvTableColumnSx.category}>{competency.category}</TableCell>
                      <TableCell sx={cvTableColumnSx.proficiency}>
                        <Chip
                          label={competency.proficiency}
                          color={
                            competency.proficiency === 'Expert' ? 'success' :
                              competency.proficiency === 'Avance' ? 'primary' :
                                competency.proficiency === 'Intermediaire' ? 'info' :
                                  'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={cvTableColumnSx.evidence}>
                        <Typography
                          variant="body2"
                          className="table-cell-truncate"
                          sx={{
                            display: 'block',
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere'
                          }}
                        >
                          {competency.evidence}
                        </Typography>
                      </TableCell>
                      {!viewOnly && (
                        <TableCell className="table-actions-cell" sx={cvTableColumnSx.actions}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleEditCompetency(competency, index, updateCustomState);
                              startEditing(competency, index);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveCompetency(index, analysisResult, setAnalysisResult)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Filter menu for category and proficiency columns */}
        {/* Category Filter Menu */}
        <Popover
          open={Boolean(customState.categoryFilterAnchorEl)}
          anchorEl={customState.categoryFilterAnchorEl}
          onClose={handleCategoryFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          hideBackdrop={true}
          disablePortal={true}
        >
          <Box sx={{ p: 1, minWidth: 150 }}>
            <MenuItem
              value=""
              onClick={() => handleCategoryFilterSelect('')}
              sx={{
                fontWeight: customState.categoryFilter === '' ? 'bold' : 'normal'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {t('common.all')}
                {customState.categoryFilter === '' ?
                  <FilterAltIcon fontSize="small" color="primary" /> : null}
              </Box>
            </MenuItem>

            {categories.map((category) => (
              <MenuItem
                key={category}
                value={category}
                onClick={() => handleCategoryFilterSelect(category)}
                sx={{ fontWeight: customState.categoryFilter === category ? 'bold' : 'normal' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {category}
                  {customState.categoryFilter === category ?
                    <FilterAltIcon fontSize="small" color="primary" /> : null}
                </Box>
              </MenuItem>
            ))}
          </Box>
        </Popover>

        {/* Proficiency Filter Menu */}
        <Popover
          open={Boolean(customState.proficiencyFilterAnchorEl)}
          anchorEl={customState.proficiencyFilterAnchorEl}
          onClose={handleProficiencyFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          hideBackdrop={true}
          disablePortal={true}
        >
          <Box sx={{ p: 1, minWidth: 150 }}>
            <MenuItem
              value=""
              onClick={() => handleProficiencyFilterSelect('')}
              sx={{
                fontWeight: customState.proficiencyFilter === '' ? 'bold' : 'normal'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {t('common.all')}
                {customState.proficiencyFilter === '' ?
                  <FilterAltIcon fontSize="small" color="primary" /> : null}
              </Box>
            </MenuItem>

            {proficiencyLevels.map((level) => (
              <MenuItem
                key={level}
                value={level}
                onClick={() => handleProficiencyFilterSelect(level)}
                sx={{ fontWeight: customState.proficiencyFilter === level ? 'bold' : 'normal' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {level}
                  {customState.proficiencyFilter === level ?
                    <FilterAltIcon fontSize="small" color="primary" /> : null}
                </Box>
              </MenuItem>
            ))}
          </Box>
        </Popover>
      </Box>
    );
  };

  // Component for the edit dialog
  const EditDialog = ({ editingItem, editingIndex, options }) => {
    const { open, onClose, onSave } = options;
    const isNewCompetency = editingIndex === -1;

    // Local state for the editing competency - always initialize with a default value
    const [editingCompetency, setEditingCompetency] = useState(editingItem || {
      competency: '',
      category: 'Competences techniques',
      proficiency: 'Intermediaire',
      evidence: ''
    });

    // State for categories loaded from database
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [categoryLoadError, setCategoryLoadError] = useState('');

    // Load categories from database when dialog opens
    useEffect(() => {
      const loadCategories = async () => {
        try {
          setLoadingCategories(true);
          setCategoryLoadError('');
          const categoryNames = await CVAnalyzerService.getCategories();
          setCategories(categoryNames);
          logger.info('[EditDialog]', `Loaded ${categoryNames.length} categories from database`, categoryNames);
        } catch (error) {
          logger.error('[EditDialog]', 'Error loading categories:', error);
          setCategories([]);
          setCategoryLoadError(error.message || t('errors.genericError'));
        } finally {
          setLoadingCategories(false);
        }
      };

      if (open) {
        loadCategories();
      }
    }, [open]);

    // Update state when editingItem changes
    useEffect(() => {
      if (editingItem) {
        setEditingCompetency(editingItem);
      }
    }, [editingItem]);

    // Don't render if no editing item
    if (!editingItem) return null;

    // Handle input changes
    const handleInputChange = (field, value) => {
      setEditingCompetency(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const handleSave = () => {
      if (onSave) {
        onSave(editingCompetency);
      }
      onClose();
    };

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {isNewCompetency ? t('cvAnalyzer.addCompetency') : t('cvAnalyzer.editCompetency')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={t('cvAnalyzer.competency')}
              value={editingCompetency.competency || ''}
              onChange={(e) => handleInputChange('competency', e.target.value)}
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('cvAnalyzer.category')}</InputLabel>
                <Select
                  value={editingCompetency.category || (categories.length > 0 ? categories[0] : 'Competences techniques')}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="Category"
                  disabled={loadingCategories}
                >
                  {loadingCategories ? (
                    <MenuItem value="" disabled>Loading categories...</MenuItem>
                  ) : (
                    categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('cvAnalyzer.proficiency')}</InputLabel>
                <Select
                  value={editingCompetency.proficiency || 'Intermediaire'}
                  onChange={(e) => handleInputChange('proficiency', e.target.value)}
                  label="Proficiency"
                >
                  <MenuItem value="Theorique">{t('cvAnalyzer.theoretical')}</MenuItem>
                  <MenuItem value="Debutant">{t('cvAnalyzer.beginner')}</MenuItem>
                  <MenuItem value="Intermediaire">{t('cvAnalyzer.intermediate')}</MenuItem>
                  <MenuItem value="Avance">{t('cvAnalyzer.advanced')}</MenuItem>
                  <MenuItem value="Expert">{t('cvAnalyzer.expert')}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {categoryLoadError && (
              <Typography variant="body2" color="error">
                {t('cvAnalyzer.categoryLoadError')}
              </Typography>
            )}

            <TextField
              label={t('cvAnalyzer.evidenceContext')}
              value={editingCompetency.evidence || ''}
              onChange={(e) => handleInputChange('evidence', e.target.value)}
              multiline
              rows={4}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Render function for the edit dialog
  const renderEditDialog = (editingItem, editingIndex, options) => {
    return <EditDialog editingItem={editingItem} editingIndex={editingIndex} options={options} />;
  };

  // The main component using shared AnalyzerPage
  return (
    <AnalyzerPage
      key={renderKey}
      type="cv"
      title={t('cvAnalyzer.title')}
      steps={[
        t('cvAnalyzer.uploadCV'),
        t('cvAnalyzer.reviewAnalysis')
      ]}
      userId={userId}
      viewOnly={viewOnly}
      onComplete={onComplete}
      onStatusChange={onStatusChange}
      renderDataDisplay={renderDataDisplay}
      renderEditDialog={renderEditDialog}
      handleSaveEdit={handleSaveEdit}
      getInitialState={getInitialState}
      processFiles={processFiles}
    />
  );
}

export default CVAnalyzerPage;
